/**
 * 动态路由生成器
 * 根据菜单数据动态生成路由配置
 */

import { COMPONENT_MAP } from '@/config/componentMap'

/**
 * 根据菜单数据生成路由配置
 * @param {Array} menus 菜单数据数组
 * @returns {Array} 路由配置数组
 */
export function generateRoutesFromMenus(menus) {
  if (!Array.isArray(menus)) {
    return []
  }

  return menus
    .filter(menu => {
      // 规范化 isShow 和 status 的值
      // 支持: 1, "1", true, "true"
      const isShowVal = menu.isShow
      const statusVal = menu.status
      
      const isShow = isShowVal === 1 || isShowVal === '1' || isShowVal === true || isShowVal === 'true'
      // 如果 status 未定义，默认为启用；否则需严格检查值
      const status = statusVal === undefined || statusVal === null || statusVal === 1 || statusVal === '1' || statusVal === true || statusVal === 'true'
      
      if (!isShow || !status) {
        // 降低日志级别，避免控制台刷屏
        console.debug(`菜单被过滤: ${menu.menuTitle || menu.menuName}`, { 
          isShow: isShowVal, 
          status: statusVal
        })
      }
      
      return isShow && status
    }) // 只处理显示且启用的菜单
    .map(menu => generateSingleRoute(menu))
    .filter(route => route !== null) // 过滤掉无效的路由
}

/**
 * 生成单个路由配置
 * @param {Object} menu 菜单数据
 * @returns {Object|null} 路由配置对象
 */
function generateSingleRoute(menu) {
  // 菜单类型：1=目录，2=菜单，3=按钮
  // 只有目录和菜单类型才生成路由，按钮类型不生成路由
  if (menu.menuType === 3) {
    return null
  }

  const route = {
    path: menu.path,
    name: menu.menuName,
    meta: {
      title: menu.menuTitle,
      requiresAuth: true,
      icon: menu.icon,
      menuId: menu.id,
      menuType: menu.menuType,
      sort: menu.sort || 0
    }
  }

  // 添加权限标识
  if (menu.perms) {
    route.meta.perms = menu.perms
  }

  // 添加重定向
  if (menu.redirect) {
    route.redirect = menu.redirect
  }

  // 如果是菜单类型且有组件，添加组件
  if (menu.menuType === 2 && menu.component) {
    if (COMPONENT_MAP[menu.component]) {
      route.component = COMPONENT_MAP[menu.component]
    } else {
      console.warn(`组件 ${menu.component} 在组件映射表中不存在，菜单：${menu.menuTitle}`)
      // 如果组件不存在，可以设置一个默认的错误页面
      route.component = () => import('@/views/ErrorPage.vue').catch(() => {
        // 如果错误页面也不存在，返回一个简单的组件
        return {
          template: '<div class="error-page"><h2>页面不存在</h2><p>组件 {{ componentName }} 未找到</p></div>',
          data() {
            return {
              componentName: menu.component
            }
          }
        }
      })
    }
  } else if (menu.menuType === 1 && !menu.component) {
    // 如果是目录类型且没有指定组件，则添加默认的布局组件
    // 这里使用一个简单的渲染函数组件，直接渲染 router-view
    route.component = {
      render: h => h('router-view')
    }
  }

  // 如果有子菜单，递归处理
  if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
    const childRoutes = generateRoutesFromMenus(menu.children)
    if (childRoutes.length > 0) {
      // 关键修改：如果父级有组件（比如目录的 router-view），子路由作为 children 挂载
      // 如果父级没有组件（纯逻辑分组），子路由应该扁平化（但这里为了保持菜单结构，我们通常希望是嵌套的）
      // 现在的逻辑是：目录级菜单会被赋予 router-view 组件，所以子路由可以正常作为 children
      route.children = childRoutes
      
      // 额外处理：如果父路径不以 / 开头（相对路径），子路径需要处理吗？
      // Vue Router 默认子路径如果是绝对路径（以 / 开头）会从根路径开始匹配
      // 如果是相对路径，会拼接父路径
      // 这里我们假设后端返回的 path 都是完整的绝对路径
      // 为了让嵌套路由生效，子路由的 path 应该是相对于父路由的，或者父路由 path 只是前缀
      
      // 修正：如果后端返回的子菜单 path 是完整的绝对路径（如 /referral/level）
      // 而父菜单 path 是 /referral
      // 在 Vue Router 的 children 配置中，子路由 path 如果以 / 开头，会被当作根路径
      // 这会导致 <router-view> 无法正确匹配
      // 所以我们需要把子路由的 path 处理成相对路径，或者让父路由仅仅作为一个不带 path 的布局容器？
      // 不，通常后台管理系统的做法是：
      // 父路由 /referral -> LayoutComponent
      // 子路由 level -> /referral/level
      
      // 让我们检查一下生成的子路由 path
      route.children = route.children.map(child => {
        // 在 Vue Router 中，如果你在 children 数组里写了绝对路径（比如 path: '/referral/level'），
        // 它是合法的，Router 会把它作为绝对路径匹配，但同时它依然是当前父路由的嵌套子路由，
        // 即：它会渲染在父组件的 <router-view> 中。
        // 但如果你把它截断成 'level'，而我们之前的兜底跳转逻辑拿到的是这个 'level'，
        // 那么 router.push('level') 会被当作相对路径解析，导致从 /login 跳转到 /level 而不是 /referral/level，
        // 这就是为什么白屏的原因！
        
        // 所以我们不需要（也不应该）去截断后端的绝对路径，保留完整的绝对路径。
        return child
      })
    }
  }

  // 关键修改：如果路由组件为空（即目录类型），且没有指定component
  // 此时如果它作为顶层路由（path以/开头），且parentRouteName为AdminLayout
  // 那么它应该使用AdminLayout作为组件，而不是router-view
  // 但我们现在的逻辑是把所有路由都挂载到AdminLayout下的
  
  // 问题在于：
  // 1. 父路由 /referral (router-view)
  // 2. 子路由 level (ReferralLevel)
  // 当我们 addRoutes 到 AdminLayout 时，结构变成了：
  // AdminLayout
  //   - /referral (router-view)
  //       - level (ReferralLevel)
  
  // 访问 /referral/level 时：
  // AdminLayout 渲染 -> router-view (显示 /referral)
  // /referral 渲染 -> router-view (显示 level)
  // 理论上是可行的。
  
  // 但是用户反馈“没有顶部导航栏和左侧菜单区域”，说明 AdminLayout 根本没渲染！
  // 这意味着 /referral 并没有作为 AdminLayout 的子路由，而是作为根路由渲染了。
  
  // 让我们检查 addDynamicRoutes 函数中的 addRouteCompat 逻辑。
  
  return route
}

/**
 * 将动态路由添加到现有路由中
 * @param {Object} router Vue Router 实例
 * @param {Array} menus 菜单数据
 * @param {string} parentRouteName 父路由名称，默认为 AdminLayout
 */
export function addDynamicRoutes(router, menus, parentRouteName = 'AdminLayout') {
  const dynamicRoutes = generateRoutesFromMenus(menus)
  const existingNames = (() => {
    if (typeof router.getRoutes === 'function') {
      return new Set(router.getRoutes().map(r => r.name))
    }
    const names = new Set()
    const traverse = (arr) => {
      if (!Array.isArray(arr)) return
      arr.forEach(r => {
        if (r && r.name) names.add(r.name)
        if (r && Array.isArray(r.children)) traverse(r.children)
      })
    }
    traverse(router && router.options && router.options.routes)
    return names
  })()
  
  const hasV4API = typeof router.addRoute === 'function'
  // const addRouteCompat = (parentName, route) => { ... } // 移除未使用的变量
  
  // 修改动态路由的组件配置
  // 仅针对顶层路由（那些将要被挂载到 AdminLayout 下的）
  // ... (保留注释以解释逻辑) ...

  function addRoutesRecursive(routes, parentName) {
    routes.forEach(route => {
      const isExisting = route.name && existingNames.has(route.name)
      
      if (isExisting) {
         if (route.children && route.children.length > 0) {
          addRoutesRecursive(route.children, route.name)
        }
      } else {
        if (route.children && route.children.length > 0) {
          route.children = filterNewRoutes(route.children)
        }
        
        // 关键修复：
        // 如果 parentName 是 AdminLayout，但我们检测到无法使用 addRoute(parentName, ...)
        // (比如 existingNames 里没有，或者 API 不支持)
        // 我们就手动修改 route 的 component 为 AdminLayout
        // 注意：这只适用于 route 本身是目录（router-view）的情况。
        
        let finalRoute = route
        const useAdminLayoutWrapper = parentName === 'AdminLayout' && (!hasV4API || !existingNames.has(parentName))
        
        if (useAdminLayoutWrapper) {
           // 降低日志级别，避免控制台刷屏
           console.debug(`自动包裹 Layout 组件: ${route.path}`)
           // 动态导入 AdminLayout
           const AdminLayout = () => import('@/layouts/AdminLayout.vue')
           
           if (route.children) {
             // 如果是目录，直接把组件改为 AdminLayout
             // 原来的 component 是 { render: h => h('router-view') }
             // AdminLayout 本身就包含 router-view，所以这是兼容的
             finalRoute = {
               ...route,
               component: AdminLayout
             }
           } else {
              // 如果是页面，简单起见，我们也改为 AdminLayout
              // 但这可能会导致页面内容无法渲染（因为 AdminLayout 渲染 children，而 route 没有 children）
              // 这里假设顶层动态路由通常都是目录结构
              finalRoute = {
                ...route,
                component: AdminLayout
              }
           }
        }

        try {
          // 强制使用 addRoute (Vue Router 3.5+)
          if (hasV4API && parentName && existingNames.has(parentName)) {
             router.addRoute(parentName, finalRoute)
          } else {
             // 降级方案：如果无法挂载到父路由，且这是顶层路由
             if (parentName === 'AdminLayout') {
                const AdminLayout = () => import('@/layouts/AdminLayout.vue')
                // 只有当它是目录类型（component 是 router-view 占位符）时，才替换为 AdminLayout
                // 此时它就像是一个独立的 Layout 路由
                if (finalRoute.component && finalRoute.component.render) {
                   finalRoute.component = AdminLayout
                }
             }
             if (router.addRoute) {
               router.addRoute(finalRoute)
             } else {
               router.addRoutes([finalRoute])
             }
          }
        } catch (error) {
          console.error(`添加动态路由失败: ${route.path}`, error)
        }
      }
    })
  }

  function filterNewRoutes(routes) {
    return routes.filter(route => {
      if (route.name && existingNames.has(route.name)) {
        return false // 过滤掉已存在的路由
      }
      if (route.children && route.children.length > 0) {
        route.children = filterNewRoutes(route.children)
      }
      return true
    })
  }

  addRoutesRecursive(dynamicRoutes, parentRouteName)
}

/**
 * 清除动态路由
 * @param {Object} router Vue Router 实例
 * @param {Array} routeNames 要清除的路由名称数组
 */
export function removeDynamicRoutes(router, routeNames) {
  if (!Array.isArray(routeNames)) {
    return
  }

  routeNames.forEach(routeName => {
    try {
      if (typeof router.removeRoute === 'function' && typeof router.hasRoute === 'function') {
        if (router.hasRoute(routeName)) {
          router.removeRoute(routeName)
          console.log(`动态路由已移除: ${routeName}`)
        }
      } else {
        // vue-router v3 无移除 API，直接跳过
      }
    } catch (error) {
      console.error(`移除动态路由失败: ${routeName}`, error)
    }
  })
}

/**
 * 从菜单数据中提取所有路由名称
 * @param {Array} menus 菜单数据
 * @returns {Array} 路由名称数组
 */
export function extractRouteNames(routes) {
  if (!Array.isArray(routes)) {
    return []
  }

  const routeNames = []
  
  function extractNames(routeList) {
    routeList.forEach(route => {
      if (route.name) {
        routeNames.push(route.name)
      }
      
      if (route.children && Array.isArray(route.children)) {
        extractNames(route.children)
      }
    })
  }
  
  extractNames(routes)
  return routeNames
}

/**
 * 验证菜单路由配置
 * @param {Array} menus 菜单数据
 * @returns {Object} 验证结果
 */
export function validateMenuRoutes(menus) {
  const errors = []
  const warnings = []
  
  function validateMenu(menu, parentPath = '') {
    // 检查必要字段
    if (!menu.menuName) {
      errors.push(`菜单 "${menu.menuTitle}" 缺少 menuName 字段`)
    }
    
    if (!menu.path) {
      errors.push(`菜单 "${menu.menuTitle}" 缺少 path 字段`)
    }
    
    // 检查组件是否存在
    if (menu.menuType === 2 && menu.component) {
      if (!COMPONENT_MAP[menu.component]) {
        warnings.push(`菜单 "${menu.menuTitle}" 的组件 "${menu.component}" 在组件映射表中不存在`)
      }
    }
    
    // 检查路径格式
    if (menu.path && !menu.path.startsWith('/') && parentPath) {
      warnings.push(`菜单 "${menu.menuTitle}" 的路径 "${menu.path}" 建议使用绝对路径`)
    }
    
    // 递归检查子菜单
    if (menu.children && Array.isArray(menu.children)) {
      menu.children.forEach(child => validateMenu(child, menu.path))
    }
  }
  
  if (Array.isArray(menus)) {
    menus.forEach(menu => validateMenu(menu))
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}
